import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Switch,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import Loader from '../../appLoader';
import { FC, useMemo, useRef, useState } from 'react';
import { QUERY_KEYS, useAddUpdateProjectsMutation, useTableView } from '../api';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { FaPlus, FaSync } from 'react-icons/fa';
import { goToLink } from '../helpers/integrationDetails.utils';
import { useToastHook } from '../../appToast';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import IntegrationModal from '../IntegrationModal';
import ConfirmModal from '../confirmModal/confirmModal';
import { SearchIcon, SettingIcon } from '../../icons';
import { StyledLink } from '../../StyledLink';
import { useDebounce } from '../../hooks/useDebounce';
import { useVirtualizer } from '@tanstack/react-virtual';
import { getToken, getOrgId } from '@devd-client/api';

interface TableViewProps {
  intDetailsData: any;
  openIntegrationModal: boolean;
  handleIntegrateModalOpen: () => void;
  handleIntegrateModalClose: () => void;
  openConfirmModal: boolean;
  handleConfirmModalOpen: () => void;
  headerText: string;
  bodyText: string;
  isManager?: boolean;
}

export const TableView: FC<TableViewProps> = ({
  intDetailsData,
  openIntegrationModal,
  handleIntegrateModalOpen,
  handleIntegrateModalClose,
  openConfirmModal,
  handleConfirmModalOpen,
  headerText,
  bodyText,
  isManager = false,
}) => {
  const integrationId = intDetailsData?.dto?.name;
  const { data, isLoading } = useTableView(integrationId);
  const [Loading, setIsLoading] = useState(false);
  const [newToast] = useToastHook();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { mutate: handleRepoState, isLoading: setRepoStateLoading } =
    useAddUpdateProjectsMutation();

  const markRepoState = (repoName: any, flag: boolean) => {
    handleRepoState(
      {
        integrationId: integrationId,
        data: {
          id: repoName.key.id,
          active: flag,
          integrationKey: repoName.key.integrationKey,
        },
      },
      {
        onSuccess: () => {
          newToast({
            message:
              'Repo status updated. Changes will reflect in upcoming data.',
            status: 'success',
          });
          queryClient.invalidateQueries([QUERY_KEYS.tableView]);
        },
        onError: (err: Error | undefined | unknown) => {
          newToast({
            message: (err as Error).message || 'Something went wrong!',
            status: 'error',
          });
        },
      }
    );
  };

  const columnHelper = createColumnHelper<any>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        id: 'name',
        cell: (info) => (
          <Box
            minWidth={'200px'}
            maxWidth={'100%'}
            overflow="auto"
            whiteSpace="normal"
          >
            {info.getValue()}
          </Box>
        ),
        header: () => (
          <Box whiteSpace="nowrap" textTransform={'none'}>
            Repository Name
          </Box>
        ),
        size: 30,
      }),
      columnHelper.accessor('additionalParam.path_with_namespace', {
        id: 'namespace',
        cell: (info) => (
          <Box
            minWidth={'200px'}
            maxWidth={'100%'}
            overflow="auto"
            whiteSpace="normal"
          >
            {info.getValue() || '-'}
          </Box>
        ),
        header: () => (
          <Box whiteSpace="nowrap" textTransform={'none'}>
            Namespace
          </Box>
        ),
        size: 30,
      }),
      columnHelper.accessor('external_link', {
        id: 'url',
        cell: (info) => (
          <Box
            minWidth={'200px'}
            maxWidth={'100%'}
            overflow="auto"
            whiteSpace="normal"
          >
            <StyledLink path={info.getValue()} target="_blank">
              {info.getValue()}
            </StyledLink>
          </Box>
        ),
        header: () => (
          <Box whiteSpace="nowrap" textTransform={'none'}>
            URL
          </Box>
        ),
        size: 40,
      }),
      columnHelper.accessor('active', {
        id: 'active',
        cell: (info) => (
          <Box width={'80px'}>
            <Tooltip
              label={info.getValue() ? 'Disable tracking' : 'Enable Tracking'}
              hasArrow
              placement="bottom-end"
            >
              <Flex justify={'center'}>
                <Switch
                  id={`switch-${info.row.id}`}
                  isChecked={info.getValue()}
                  onChange={() => {
                    markRepoState(info?.row?.original, !info.getValue());
                  }}
                  isDisabled={isManager || setRepoStateLoading}
                />
              </Flex>
            </Tooltip>
          </Box>
        ),
        header: () => (
          <Box whiteSpace="nowrap" textAlign="center" textTransform={'none'}>
            Active
          </Box>
        ),
        size: 10,
      }),
      columnHelper.accessor('', {
        id: 'settings',
        cell: (info) => (
          <Flex justify={'center'} width={'80px'}>
            <SettingIcon
              height={'16px'}
              width={'16px'}
              color="gray"
              cursor={isManager ? 'not-allowed' : 'pointer'}
              onClick={
                isManager
                  ? undefined
                  : () =>
                      navigate(
                        `/settings/integrations/${intDetailsData?.dto?.displayName}/${info?.row?.original?.name}/${info?.row?.original?.key?.id}`
                      )
              }
              opacity={isManager ? 0.5 : 1}
            />
          </Flex>
        ),
        header: () => (
          <Box whiteSpace="nowrap" textAlign="center" textTransform={'none'}>
            Settings
          </Box>
        ),
        size: 10,
      }),
    ],
    [
      intDetailsData?.dto?.displayName,
      markRepoState,
      navigate,
      setRepoStateLoading,
    ]
  );

  const filteredRows = useMemo(() => {
    const sourceData = data?.dto || [];
    if (!debouncedSearchTerm.trim()) {
      return sourceData;
    }

    const query = debouncedSearchTerm.toLowerCase().trim();
    return sourceData.filter((row: any) => {
      const name = (row.name || '').toLowerCase();
      const namespace = (
        row.additionalParam?.path_with_namespace || ''
      ).toLowerCase();

      return name.includes(query) || namespace.includes(query);
    });
  }, [data?.dto, debouncedSearchTerm]);

  const table = useReactTable({
    data: filteredRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const { rows } = table.getRowModel();
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 80,
    measureElement:
      typeof window !== 'undefined' &&
      navigator.userAgent.indexOf('Firefox') === -1
        ? (element: any) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start || 0 : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - (virtualRows[virtualRows.length - 1]?.end || 0)
      : 0;

  return (
    <>
      {openIntegrationModal && (
        <IntegrationModal
          isOpen={openIntegrationModal}
          handleIntegrateModalClose={handleIntegrateModalClose}
          intDetailsData={intDetailsData}
          handleConfirmModalOpen={handleConfirmModalOpen}
        />
      )}
      {openIntegrationModal === false && openConfirmModal && (
        <ConfirmModal
          isOpen={openConfirmModal}
          headerText={headerText}
          bodyText={bodyText}
        />
      )}

      <Box mt={6}>
        <Flex justifyContent="space-between" mb={2}>
          <InputGroup maxWidth="300px">
            <InputLeftElement pointerEvents="none" pb={2}>
              <SearchIcon />
            </InputLeftElement>
            <Input
              placeholder="Search repository or namespace"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="sm"
            />
          </InputGroup>
          <Flex>
            {intDetailsData?.dto?.name === 'GitLab' && (
              <Button
                variant="primary"
                size="sm"
                mr="10px"
                onClick={handleIntegrateModalOpen}
                isDisabled={isManager}
              >
                <Icon as={FaPlus} mr={2} />
                Add new account
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              mr="10px"
              isDisabled={isManager}
              onClick={() => {
                if (
                  intDetailsData?.dto?.links?.resync_url.startsWith(
                    'https://api.devdynamics.ai'
                  )
                ) {
                  try {
                    setIsLoading(true);
                    fetch(
                      intDetailsData?.dto?.links?.resync_url
                        .replace('{integrationKey}', integrationId)
                        .replace('{orgId}', `${getOrgId()}`),
                      {
                        method: 'GET',
                        headers: {
                          Authorization: `bearer ${getToken()}`,
                        },
                      }
                    )
                      .then((res) => {
                        setIsLoading(false);
                        newToast({
                          message:
                            'Request submitted successfully. Please check after some time.',
                          status: 'success',
                        });
                      })
                      .catch((err) => {
                        setIsLoading(false);
                        newToast({
                          message: 'There was a problem contact support.',
                          status: 'error',
                        });
                      });
                  } catch (error) {
                    setIsLoading(false);
                  }
                } else {
                  goToLink(
                    intDetailsData?.dto?.links?.resync_url
                      .replace('{integrationKey}', integrationId)
                      .replace('{orgId}', `${getOrgId()}`)
                  );
                }
              }}
              justifyContent="flex-end"
            >
              <Icon as={FaSync} mr={2} />
              Sync Repositories
            </Button>
          </Flex>
        </Flex>
        {isLoading || Loading ? (
          <Loader />
        ) : (
          <Box
            ref={tableContainerRef}
            height="30rem"
            overflowY="auto"
            border="1px solid"
            borderColor={'text.tertiary'}
            borderRadius="md"
          >
            <Table layout="fixed" width="100%">
              <Thead
                position="sticky"
                top={0}
                bg="white"
                zIndex={1}
                boxShadow="0 1px 0 0 var(--chakra-colors-text-tertiary)"
              >
                {table.getHeaderGroups().map((headerGroup) => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      let width;
                      if (header.id === 'name' || header.id === 'namespace') {
                        width = '200px';
                      } else if (header.id === 'url') {
                        width = '250px';
                      } else {
                        width = '80px';
                      }

                      return (
                        <Th
                          key={header.id}
                          width={width}
                          style={{ width }}
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                          borderRight="1px solid"
                          borderColor={'text.tertiary'}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </Th>
                      );
                    })}
                  </Tr>
                ))}
              </Thead>
              <Tbody>
                {paddingTop > 0 && (
                  <Tr>
                    <Td
                      height={`${paddingTop}px`}
                      padding={0}
                      colSpan={columns.length}
                    />
                  </Tr>
                )}
                {virtualRows.map((virtualRow) => {
                  const row = rows[virtualRow.index];
                  return (
                    <Tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <Td
                          key={cell.id}
                          borderRight="1px solid"
                          borderBottom="1px solid"
                          borderColor={'text.tertiary'}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </Td>
                      ))}
                    </Tr>
                  );
                })}
                {paddingBottom > 0 && (
                  <Tr>
                    <Td
                      height={`${paddingBottom}px`}
                      padding={0}
                      colSpan={columns.length}
                    />
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>
    </>
  );
};

export default TableView;
